/*
  *+------------------------------------------------------------------------+
  *| Licensed Materials - Property of IBM
  *| IBM Cognos Products: Dashboard
  *| (C) Copyright IBM Corp. 2019, 2021
  *|
  *| US Government Users Restricted Rights - Use, duplication or disclosure
  *| restricted by GSA ADP Schedule Contract with IBM Corp.
  *+------------------------------------------------------------------------+
  */

define([
	'./LiveWidget',
	'../../lib/@waca/core-client/js/core-client/ui/core/Events',
	'../../lib/@waca/core-client/js/core-client/utils/LoadCSSPromise',
	'./nls/StringResources',
	'underscore',
	'dashboard-core/js/canvas/Content',
	'dashboard-core/js/dashboard/model/LayoutModel',
	'dashboard-core/js/features/content/InlineFeatures',
	'./models/LiveWidgetModel'
], function(LiveWidget, Events, LoadCSSPromise, StringResources, _, ContentImpl, LayoutModel, InlineFeatures, LiveWidgetModel) {
	let isCSSLoaded = false;

	class StandaloneLiveWidget {

		/**
		 * Create an instance of the live widget API
		 *
		 * @constructor
		 * @param {Object} options.node Dom node where the live widget content will be created
		 * @param {Object} options.glassContext Glass context that provide access to glass API
		 * @param {Object} options.spec The live widget spec json.
		 * @param {Object} options.inlineContentFeatures Content features to be loaded inline
		 * @param {QueryResultsAPI} options.queryResults If provided, the LiveWidgetPreview will not issue a query for data and will use the queryResults instead

		 * @example
		 *  let LiveWidget = new LiveWidgetAPI({
	   * 	node: document.getElementById('containerDivId'),
	   * 	glassContext: this.glassContext,
		 * 	inlineContentFeatures: [{
		 *		"name": "QueryResultOverride",
		 *		"id": "com.ibm.bi.dashboard.live-features.queryResultOverride",
		 *		"class": "dashboard-analytics/features/widget/queryResultOverride/queryResultOverride"
		 *	}],
		 *   spec: {
	   *     "data": {
	   *              "dataViews": [
	   *                  {
	   *                      "model":{
	   *                      		"assetId": "iB3884FE9ACF64BD2B8F0879A8C34B978",
	   *                      		"type": "uploadedFile"
	   *                      }
	   *                      "dataItems": [
	   *                          {
	   *                              "itemId": "HollywoodMovies_xls.Year_",
	   *
	   *                            },
	   *                          {
	   *                              "itemId": "HollywoodMovies_xls.Rotten_Tomatoes"
	   *                          }
	   *                      ]
	   *                  }
	   *              ]
	   *          }
	   *      }
	   * });
		 */
		constructor(options) {
			this._destroyed = false;
			this.spec = JSON.parse(JSON.stringify(options.spec));
			this.spec.id = _.uniqueId('LW');
			this.spec.type = 'live';
			this.board = options.board;
			this.node = options.node;
			this._glassContext = options.glassContext;
			this.runtimeEnv = options.runtimeEnv;
			this.predictData = options.predictData;
			this.isPredictPreview = options.isPredictPreview;
			this._inlineContentFeatures = options.inlineContentFeatures || [];
			this._queryResults = options.queryResults;
			this._managesOwnQueries = options.managesOwnQueries;
			this._LayoutModelClass = options.layoutModelClass || LayoutModel;
			this._featuresOverride = options.featuresOverride;

			// TODO - temporary until explore starting sending the dashboard Api
			if (this.runtimeEnv && !this.runtimeEnv.dashboardApi){
				this.runtimeEnv.dashboardApi = this._glassContext.appController.getCurrentContentView().getDashboardApi();
			}

			if (this.runtimeEnv && !this.runtimeEnv.dashboardFeatureLoader){
				// Needed to be able to build a content API for the live widget preview
				this.runtimeEnv.dashboardFeatureLoader = this.runtimeEnv.dashboardApi.getFeature('DashboardFeatureLoader.internal');
			}

			this._defaultEventRouter = new Events();

			this.interactivitySettings = {
				isClickDisabled: !options.enableClick,
				isPanAndZoomDisabled: !options.enablePanAndZoom,
				isHoverDisabled: !options.enableHover,
				tabNavigation: options.tabNavigation
			};
			this.featureSet = options.featureSet;
		}

		_createEnvironment() {
			if (this.runtimeEnv) {
				return Promise.resolve();
			}

			return this._glassContext.getSvc('.DashboardRuntime').then((svc) => {
				const options = {
					spec: svc.getDefaultSpec(),
					profiles: ['core', 'consume']
				};
				options.spec.widgets[this.spec.id] = this.spec;
				options.spec.name = this.board && this.board.name || options.spec.name;
				options.spec.version = this.board && this.board.version || options.spec.version;
				options.featuresOverride = this._featuresOverride;
				return svc.getRuntimeEnvironment(options).then((env) => {
					// overwrite with the upgraded spec
					this.spec = env.boardModel.widgets[this.spec.id];

					let promises = [];
					this.runtimeEnv = env;
					if (env.cssStyles && !isCSSLoaded) {
						isCSSLoaded = true;
						env.cssStyles.forEach((style) => {
							promises.push(LoadCSSPromise.load(style, null, null, [{
								type: 'text/css'
							}]));
						});
					}
					return Promise.all(promises);
				});
			});

		}

		/**
		 * Render the live widget instance. This method will execute the query, wait for it and render when the data is complete.
		 * @function LiveWidgetAPI#render
		 * @params {options} object that describes the live widget
		 * @return {Promise} promise that will be resolved when the render is complete or reject in the case of an error
		 * @example
		 * liveWidget.render().then(()=>{
		 *    console.debug('render is complete');
		 * }).catch(()=>{
		 *    console.debug('An error occured');
		 * });
		 */
		render(options = {}) {
			return this._createEnvironment()
				.then(() => {
					return this._updateModelIds();
				})
				.then(() => {
					return this._createContent(options);
				})
				.then((content) => {
					if (this._queryResults) {
						this._inlineContentFeatures = this._addQueryOverrideFeature();
					}
					return Promise.all([content, content.registerFeatures(this._inlineContentFeatures)]);
				})
				.then(([content]) => {
					if (this._queryResults) {
						content.getFeature('QueryResultOverride').setQueryResults(this._queryResults);
					}
					options.widgetModel = content.widgetModel;
					options.initialConfigJSON = content.widgetModel;
					options = _.extend(options, {
						dashboardApi: this.runtimeEnv.dashboardApi,
						eventRouter: this.runtimeEnv.eventRouter || this._defaultEventRouter,
						registry: this.runtimeEnv.dashboardApi.getWidgetRegistry ? this.runtimeEnv.dashboardApi.getWidgetRegistry().live : {},
						contentFeatureLoader: this._createContentFeatureLoader(content),
						content: content
					});
					let widgetReady;
					if (!this.widget) {
						this.contentNode = this.contentNode || options.el;
						this.node.appendChild(this.contentNode);
						// Mock ability for unit tests
						const LiveWidgetClass = this._liveWidgetClass || LiveWidget;
						this.widget = new LiveWidgetClass(options);
						widgetReady = this.widget.initialize();
					} else {
						widgetReady = Promise.resolve();
					}
					return widgetReady;
				})
				.then(() => {
					if (!this._destroyed) {
						return this.widget.onContainerReady({
							model:this.widget.model
						});
					}
				})
				.then(() => {
					if (!this._destroyed) {
						return this.widget.render( null /* renderOptions */, false /* isInteractive */ );
					}
				})
				.then(() => {
					if (!this._destroyed) {
						return this.widget.whenRenderComplete();
					}
				}).catch((error) => {
					const errorId = error && error.message;
					if (errorId === 'promptingIsDisabled') {
					// don't need to localize this internal error
						throw error;
					}
					const datasetName = error && error.datasetName;
					//catches the exceptions thrown from VisRenderSequence and localize it so that
					//the caller of liveWidgetPreview eg. vis-recommnder can render the exceptions;
					if (errorId && datasetName){
						const errorMessage = StringResources.get(errorId, { datasetName:datasetName } );
						throw new Error(errorMessage);
					}else{
						throw error;
					}
				});
		}

		// TODO: look at moving the content creation earlier in the object construction/initialization to provide a mechanism
		// for content feature providers to work with the content (getFeature().setXYZ())
		// Then we can move this inline feature and the _queryResults to the provider.
		_addQueryOverrideFeature() {
			this._inlineContentFeatures.push({
				containerId: 'com.ibm.bi.dashboard.content-features',
				name: 'QueryResultOverride',
				id: 'com.ibm.bi.dashboard.content-features.queryResultOverride',
				class: 'dashboard-analytics/features/widget/queryResultOverride/QueryResultOverride',
				dependencies: ['SmartAnnotationProvider', 'VisKeyDrivers'],
				types: ['widget.live'],
				profiles: ['core']
			});
			return this._inlineContentFeatures;
		}

		_createContentFeatureLoader(content) {
			return {
				whenContentReady: (/* contentId */) => {
					return Promise.resolve();
				},
				registerFeatureCollection: (contentId, collectionName) => {
					return this.runtimeEnv.dashboardApi.findGlassCollection(collectionName).then((collectionItems) => {
						return content.registerFeatures(collectionItems || []);
					});
				},
				registerFeature: (contentId, featureId, featureInstance, featureSpec) => {
					return content.registerFeature(featureId, featureInstance, featureSpec);
				},
				registerDeprecatedFeature: (contentId, featureId, featureInstance) => {
					return content.registerFeature(featureId, featureInstance, {}, true);
				}
			};
		}

		_createContent({ isPreview, isPredictPreview }) {
			const dashboard = this.runtimeEnv.dashboardApi;
			return dashboard.findGlassCollection('com.ibm.bi.dashboard.content-features')
				.then((collectionItems) => {
					const contentFeatureCollection = InlineFeatures.concat(collectionItems || []);
					const boardModel = dashboard.getFeature('internal').getBoardModel();
					const logger = dashboard.getFeature('Logger');
					const layoutModel = new this._LayoutModelClass({
						type: 'widget',
						content: {
							features: {
								Visualization: {
									isPreview: !!isPreview,
									isPredictPreview: !!isPredictPreview
								}
							}
						}
					}, boardModel, logger);

					this.content = new ContentImpl({
						contentFeatureCollection: contentFeatureCollection,
						dashboardFeatures: this.runtimeEnv.dashboardFeatureLoader,
						boardModel: boardModel,
						layoutModel: layoutModel,
						canvas: dashboard.getCanvas(),
						widgetModel: new LiveWidgetModel(this.spec)
					});

					return this.content.initialize()
						.then(() => {
							return this.content;
						});
				});
		}

		/**
		 * Get preview title
		 *
		 * @return {string} livewidget preview title
		 */
		getTitle() {
			if (this._destroyed) {
				return;
			}
			return this.widget.getTitle();
		}

		/**
		 * Resize the preview. If the container size has changed since the last render,
		 * a re-render will occur without re-executing the data query.
		 */
		resize() {
			if(this._destroyed) {
				return;
			}
			if (this.widget) {
				return this.widget.resize();
			}
		}

		/**
		 * Destroy the live widget instance.
		 * This should be called when the live widget is no longer needed to free up resources.
		 * @function LiveWidgetAPI#destroy
		 */
		destroy() {
			if (this._destroyed) {
				return;
			}
			this._destroyed = true;

			if (this.content) {
				this.content.destroy();
				this.content = null;
			}

			if (this.widget) {
				this.widget.destroy();
				this.widget = null;
			}
			if (this.contentNode) {
				this.contentNode.parentNode && this.contentNode.parentNode.removeChild(this.contentNode);
				this.contentNode = null;
			}
		}

		/**
		 * Return the live widget spec. This will be the full spec and not the spec that was provide as input.
		 *
		 * @function LiveWidgetAPI#toJSON
		 */
		toJSON() {
			if (!this._destroyed) {
				return this.widget.model.toJSON();
			}
		}

		/*
		*  Private methods
		*/
		_updateModelIds() {
			const dashboardApi = this.runtimeEnv.dashboardApi;
			const dataSources = dashboardApi.getFeature('DataSources');
			const dataSourceList = dataSources.getDataSourceList() || [];

			const promises = [];
			if (this.spec.data && this.spec.data.dataViews) {
				this.spec.data.dataViews.forEach(dataView => {
					if (!dataView.modelRef && dataView.model) {
						const sourceIdList = _.chain(dataSourceList)
							.filter(ds => ds.getAssetId() === dataView.model.assetId)
							.map(ds => ds.getId())
							.value();
						if (sourceIdList.length > 0) {
							dataView.modelRef = sourceIdList[0];
							delete dataView.model;
						} else {
							if (!dataView.model.type){
								promises.push(this._getAssetType(dataView.model.assetId).then((type) => {
									dataView.model.type = type;
									this._convertModelToModelRef(dataView, dataSources);
								}));
							} else{
								this._convertModelToModelRef(dataView, dataSources);
							}
						}
					}
				});
			}

			return Promise.all(promises);
		}

		_convertModelToModelRef (dataView, dataSources){
			dataView.modelRef = dataSources.addDataSource(dataView.model);
			delete dataView.model;
		}

		_getAssetType(assetId) {
			return this.runtimeEnv.dashboardApi.getGlassCoreSvc('.Ajax')
				.ajax({
					url: 'v1/objects/' + assetId + '?fields=userInterfaces,defaultName,searchPath',
					type: 'GET',
					headers: {
						'Accept': 'application/json'
					}
				})
				.then((response) => {
					if (response.data && response.data.data && response.data.data.length > 0) {
						return response.data.data[0].type;
					}
				});

		}
	}

	return StandaloneLiveWidget;
});
